import React, { createContext, useContext, useReducer, useEffect } from "react";
import axios from "axios-config"; // our custom axios instance
import { setAccessToken, clearAccessToken } from "utils/authToken";
import ConsoleHelper from "utils/ConsoleHelper";
import { useQueryClient } from "react-query";
import { login as apiLogin } from "api/auth";

const initialState = {
  user: null,
  organization: null,
  subscription: null,
  loading: true,
  isOwner: false, // will be set based on user and organization data
  // permissions: {}, // TODO: enable when permissions are implemented
};

function appReducer(state, action) {
  switch (action.type) {
    case "SET_ME":
      return {
        ...state,
        user: action.payload.user,
        organization: action.payload.organization,
        subscription: action.payload.subscription,
        // permissions: action.payload.permissions || {}, // TODO: enable when permissions are implemented
        loading: false,
      };
    case "CLEAR_ME":
      return {
        user: null,
        organization: null,
        subscription: null,
        loading: false,
        // permissions: {}, // TODO: enable when permissions are implemented
      };
    case "SET_LOADING":
      return {
        ...state,
        loading: action.payload,
      };
    case "SET_ERROR":
      return {
        ...state,
        error: action.payload,
        loading: false,
      };
    default:
      return state;
  }
}

const AppContext = createContext(initialState);

export function AppProvider({ children }) {
  const [state, dispatch] = useReducer(appReducer, initialState);
  const queryClient = useQueryClient(); // Get the QueryClient instance

  const fetchMe = async () => {
    try {
      const res = await axios.get("/users/me");
      ConsoleHelper("GET ME", res.data);
      // Suppose server returns { user, organization, subscription, accessToken, preferences, permissions }
      if (res.data.accessToken) {
        setAccessToken(res.data.accessToken); // refresh token in memory if sent
      }
      dispatch({
        type: "SET_ME",
        payload: {
          user: res.data.user,
          organization: res.data.organization,
          subscription: res.data.subscription,
          // permissions: res.data.permissions || {}, // TODO: enable when permissions are implemented
        },
      });
    } catch (err) {
      console.log("CATCH DISPATCH", err);
      dispatch({ type: "SET_ERROR", payload: "Failed to fetch user data" });
    }
  };

  useEffect(() => {
    fetchMe();
  }, []);

  // Login now saves the JWT in in-memory storage
  const login = async (credentials) => {
    try {
      const response = await apiLogin(credentials);
      const { user, organization, subscription, accessToken } = response.data;
      setAccessToken(accessToken); // store JWT securely in memory
      dispatch({
        type: "SET_ME",
        payload: { user, organization, subscription },
      });
    } catch (err) {
      ConsoleHelper(err);
      throw err; // re-throw to handle in the login form
    }
  };

  // On logout clear the in-memory access token
  const logout = async () => {
    await axios.post("/auth/logout", null, { withCredentials: true });
    clearAccessToken();
    dispatch({ type: "CLEAR_ME" });
    queryClient.clear();
  };

  // Permission helpers
  // TODO: enable when permissions are implemented
  // const hasPermission = (feature, action) => {
  //   // e.g. hasPermission('repairs', 'edit')
  //   return !!state.permissions?.[feature]?.[action];
  // };

  const isAuth = !!state.user?.id;
  const isVerified = !!state.user?.verified;
  const hasActiveSubscription = !!state.subscription?.isActive;
  // is owner?
  const isOwner = state.organization?.owner?.email === state.user?.email;

  return (
    <AppContext.Provider
      value={{
        state: { ...state, isAuth, isVerified, hasActiveSubscription, isOwner },
        dispatch,
        login,
        logout,
        fetchMe,
        // hasPermission, // TODO: enable when permissions are implemented
      }}
    >
      {children}
    </AppContext.Provider>
  );
}

export function useApp() {
  return useContext(AppContext);
}
